import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type AccountStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

interface AccountStatusData {
  account_status: AccountStatus;
  account_status_label: string;
  account_reason?: string;
  account_status_updated_at?: string;
  is_read_only: boolean;
  can_view_data: boolean;
}

interface AccountStatusContextType {
  accountStatus: AccountStatusData | null;
  isActive: boolean;
  isReadOnly: boolean;
}

const AccountStatusContext = createContext<AccountStatusContextType>({
  accountStatus: null,
  isActive: true,
  isReadOnly: false,
});

interface AccountStatusProviderProps {
  children: ReactNode;
}

export const AccountStatusProvider: React.FC<AccountStatusProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const accountStatus = useMemo<AccountStatusData | null>(() => {
    if (!user || user.type !== 'client') {
      return null;
    }

    const status = user.client?.status || user.status;
    if (!status || status === 'approved') {
      return null;
    }

    return {
      account_status: status as AccountStatus,
      account_status_label: status,
      account_reason: user.client?.status_reason,
      account_status_updated_at: user.client?.status_updated_at,
      is_read_only: true,
      can_view_data: true,
    };
  }, [user]);

  const isActive = accountStatus?.account_status === 'approved' || !accountStatus;
  const isReadOnly = accountStatus?.is_read_only ?? false;

  const value = useMemo(() => ({
    accountStatus,
    isActive,
    isReadOnly,
  }), [accountStatus, isActive, isReadOnly]);

  return (
    <AccountStatusContext.Provider value={value}>
      {children}
    </AccountStatusContext.Provider>
  );
};

export const useAccountStatus = (): AccountStatusContextType => {
  return useContext(AccountStatusContext);
};
